'use client';

/**
 * CountdownInput Component
 *
 * World-class flip clock style countdown with beautiful animations.
 * Each digit has its own card with flip effect.
 *
 * @example Basic usage
 * ```tsx
 * <CountdownInput time="00:30" />
 * ```
 *
 * @example Different sizes
 * ```tsx
 * <CountdownInput time="00:30" size="small" />
 * <CountdownInput time="00:30" size="large" />
 * ```
 */

import React from 'react';
import { cn } from '../../utils/cn';
import { ActivityIndicator } from '../activity-indicator';
import styles from './countdown-input.module.css';
import type { CountdownInputProps } from './countdown-input.types';

/**
 * Single digit card with flip animation
 */
const Digit: React.FC<{ value: string }> = ({ value }) => {
    return (
        <div className={styles.digit} data-testid="digit">
            <span className={cn(styles.digitValue, styles.digitFlip)}>{value}</span>
        </div>
    );
};

export const CountdownInput: React.FC<CountdownInputProps> = ({
    time,
    size = 'medium',
    variant = 'primary',
    showSpinner = true,
    className,
    showIf = true,
    ...props
}) => {
    if (!showIf) return null;

    const containerClasses = cn(
        styles.countdownInput,
        styles.container,

        // Size variants
        size === 'small' && styles.sizeSmall,
        size === 'medium' && styles.sizeMedium,
        size === 'large' && styles.sizeLarge,

        // Color variants - sync with ActivityIndicator
        variant === 'primary' && styles.variantPrimary,
        variant === 'secondary' && styles.variantSecondary,
        variant === 'neutral' && styles.variantNeutral,
        variant === 'inherit' && styles.variantInherit,

        className
    );

    const getSpinnerSize = () => {
        if (size === 'small') return 'small';
        if (size === 'large') return 'large';
        return 'medium';
    };

    const spinnerSize = getSpinnerSize();

    return (
        <div className={containerClasses} {...props}>
            {showSpinner && <ActivityIndicator size={spinnerSize} variant={variant} />}
            <Digit value={time} />
        </div>
    );
};
